import { API_ROUTES } from "@/constants/api";
import { QUERY_KEYS } from "@/constants/config";
import { apiGet } from "@/lib/api-client";
import { useQuery } from "@tanstack/react-query";
import type { AppRole, WorkspaceRole } from "@bizy/core";
import { createContext, useContext, useMemo, type ReactNode } from "react";

export interface ActorMembership {
  workspace_id: string;
  role: WorkspaceRole;
}

export interface Actor {
  user_id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  app_role: AppRole;
  memberships: ActorMembership[];
  is_superuser: boolean;
}

interface ActorContextValue {
  actor: Actor | null;
  loading: boolean;
  error: Error | null;
  isSuperuser: boolean;
}

const ActorContext = createContext<ActorContextValue | null>(null);

/**
 * Preview build (`main`) — there is no sign-in flow.
 *
 * The API resolves a fixed actor server-side (`PREVIEW_MODE`) and reports it here,
 * so the shell can still show who workspace actions are attributed to in the audit
 * trail. Restoring the Firebase-backed provider is the only change the UI needs to
 * become authenticated again (see `feature/saas`).
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const actorQuery = useQuery({
    queryKey: [QUERY_KEYS.me, "preview"],
    queryFn: () => apiGet<Actor>(API_ROUTES.me),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const value = useMemo<ActorContextValue>(
    () => ({
      actor: actorQuery.data ?? null,
      loading: actorQuery.isLoading,
      error: (actorQuery.error as Error | null) ?? null,
      isSuperuser: actorQuery.data?.app_role === "superuser",
    }),
    [actorQuery.data, actorQuery.error, actorQuery.isLoading],
  );

  return <ActorContext.Provider value={value}>{children}</ActorContext.Provider>;
};

export const useAuth = (): ActorContextValue => {
  const context = useContext(ActorContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider.");
  return context;
};
